import React, { useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
interface Step {
  title: string;
  content: ReactNode;
  isValid: boolean;
}
interface MultiStepFormProps {
  steps: Step[];
  onComplete: () => void;
  submitButtonText?: string;
}
export const MultiStepForm: React.FC<MultiStepFormProps> = ({
  steps,
  onComplete,
  submitButtonText = 'Submit'
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completed, setCompleted] = useState<boolean[]>(steps.map(() => false));
  const goToNextStep = () => {
    if (currentStep < steps.length - 1) {
      // Mark current step as completed
      const newCompleted = [...completed];
      newCompleted[currentStep] = true;
      setCompleted(newCompleted);
      // Go to next step
      setCurrentStep(currentStep + 1);
      // Scroll to top of form
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      // Handle form completion
      const newCompleted = [...completed];
      newCompleted[currentStep] = true;
      setCompleted(newCompleted);
      onComplete();
    }
  };
  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      // Scroll to top of form
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  const goToStep = (stepIndex: number) => {
    // Only allow going to completed steps or the current step + 1
    if (completed[stepIndex - 1] || stepIndex <= currentStep) {
      setCurrentStep(stepIndex);
      // Scroll to top of form
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  return <div className="w-full" data-id="element-1199">
      {/* Progress indicator */}
      <div className="mb-8" data-id="element-1200">
        <div className="flex items-center justify-between" data-id="element-1201">
          {steps.map((step, index) => <Fragment key={index} data-id="element-1202">
              {/* Step indicator */}
              <button onClick={() => goToStep(index)} className={`flex flex-col items-center relative ${index <= currentStep ? 'cursor-pointer' : 'cursor-not-allowed'}`} disabled={index > currentStep && !completed[index - 1]} data-id="element-1203">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${completed[index] ? 'bg-green-500 dark:bg-green-400 text-white' : index === currentStep ? 'bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white' : 'bg-gray-200 dark:bg-[#1A3330] text-gray-500 dark:text-[#7A8F8A]'}`} data-id="element-1204">
                  {completed[index] ? <CheckIcon className="w-5 h-5" data-id="element-1205" /> : <span data-id="element-1206">{index + 1}</span>}
                </div>
                <span className={`text-xs mt-2 font-medium ${index === currentStep ? 'text-gray-800 dark:text-[#F8FAF9]' : 'text-gray-500 dark:text-[#7A8F8A]'}`} data-id="element-1207">
                  {step.title}
                </span>
              </button>
              {/* Connector line between steps */}
              {index < steps.length - 1 && <div className="flex-1 h-1 mx-2 bg-gray-200 dark:bg-[#1A3330] relative" data-id="element-1208">
                  <div className={`absolute top-0 left-0 h-full bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] transition-all duration-300`} style={{
              width: completed[index] ? '100%' : '0%'
            }} data-id="element-1209" />
                </div>}
            </Fragment>)}
        </div>
      </div>
      {/* Step content */}
      <div className="bg-white dark:bg-[#162825] rounded-xl shadow-sm border border-gray-100 dark:border-[#1A3330] p-8" data-id="element-1210">
        <AnimatePresence mode="wait" data-id="element-1211">
          <motion.div key={currentStep} initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.3
        }} data-id="element-1212">
            {steps[currentStep].content}
          </motion.div>
        </AnimatePresence>
        {/* Navigation buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t border-gray-100 dark:border-[#1A3330]" data-id="element-1213">
          <button onClick={goToPreviousStep} className={`px-6 py-2 rounded-lg text-gray-600 dark:text-[#B0C1BD] font-medium ${currentStep === 0 ? 'opacity-50 cursor-not-allowed' : 'bg-gray-100 dark:bg-[#1A3330] hover:bg-gray-200 dark:hover:bg-[#0B2C2A]'}`} disabled={currentStep === 0} data-id="element-1214">
            Back
          </button>
          <button onClick={goToNextStep} disabled={!steps[currentStep].isValid} className={`px-6 py-2 rounded-lg font-medium ${steps[currentStep].isValid ? 'bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white hover:shadow-md' : 'bg-gray-300 dark:bg-[#1A3330] text-gray-500 dark:text-[#7A8F8A] cursor-not-allowed'}`} data-id="element-1215">
            {currentStep === steps.length - 1 ? submitButtonText : 'Continue'}
          </button>
        </div>
      </div>
    </div>;
};